import clsx from "clsx";
import { forwardRef, HTMLAttributes, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";
import Button from "../button";
import Card from "../card";
import Col from "../col";

export interface ColorPickerPopupProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "onChange"> {
  color?: string;
  mode?: "change" | "confirm";
  comparison?: boolean;
  onChange?: (color: string) => void;
  onCancel?: () => void;
}

const ColorPickerPopup = forwardRef<HTMLDivElement, ColorPickerPopupProps>(
  (props, ref) => {
    const { t } = useTranslation();
    const {
      color,
      className,
      mode = "change",
      comparison,
      onChange,
      onCancel,
      ...rest
    } = props;
    const [value, setValue] = useState(color ?? "");

    return (
      <Card ref={ref} {...rest} className={twMerge("w-fit", className)}>
        <Card.Body as={Col} gap="sm">
          <HexColorPicker
            color={comparison ? value : color}
            onChange={mode === "change" ? onChange : setValue}
          />
          {mode === "confirm" && comparison && (
            <div className="flex items-center gap-1">
              <span
                style={{ backgroundColor: color }}
                className={clsx("rounded-sm border w-7 h-7")}
              />
              <span
                style={{ backgroundColor: value }}
                className={clsx("rounded-sm border w-7 h-7")}
              />
            </div>
          )}
        </Card.Body>
        {mode === "confirm" && (
          <Card.Footer className="justify-end">
            <Button size="sm" variant="secondary" onClick={onCancel}>
              {t("common.button.cancel", "Cancel")}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                onChange?.(value);
                onCancel?.();
              }}
            >
              {t("common.button.confirm", "Confirm")}
            </Button>
          </Card.Footer>
        )}
      </Card>
    );
  }
);

export default ColorPickerPopup;
